import {useGetViews} from '../queries/useGetViews'
import {
    SelectComboboxInput,
    SelectComboboxPopover,
    SelectTrigger,
    SelectVirtualList
} from '@components/ui/select-atoms/SelectAtoms'
import {useTranslation} from '@/translations/i18n'
import {Spinner} from '@components/ui/spinner/Spinner'
import {useTheme} from 'styled-components'
import {ComboboxItem, ComboboxProvider, SelectProvider, MenuButton, MenuProvider} from '@ariakit/react'
import {useRef, useState} from 'react'
import {ChevronDownIcon, User01Icon, Users01Icon} from '@/components/ui/icon/Icon'
import {useSearchParams} from 'react-router-dom'
import {StyledItem, StyledSaveWrapper, StyledTriggerLabel} from './style'
import {Button} from '@/components/ui/button/Button'
import {useGetView} from '../queries/useGetView'
import {CreateViewDialog} from '../components/create-view-dialog/CreateViewDialog'
import {View, ViewEntity} from '../types'
import {RowMenu} from '../components/row-menu/RowMenu'
import {useEditView} from '../queries/useEditView'
import {SaveViewDialog} from '../components/SaveViewDialog'
import {errorHandler} from '@/utilities/helpers'
import {Menu} from '@/components/ui/menu-atoms/MenuAtoms'
import {SaveViewAsNewDialog} from '../components/save-view-as-new-dialog/SaveViewAsNewDialog'
import {UnsavedBlocker} from '../components/unsaved-blocker/UnsavedBlocker'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {toast} from '@/utilities/toast/toast'

export const ViewSelect = ({
    entity,
    searchInputRef
}: {
    entity: ViewEntity
    searchInputRef: React.RefObject<HTMLInputElement>
}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const [search, setSearch] = useState('')
    const [searchParams, setSearchParams] = useSearchParams()
    const viewsQuery = useGetViews({
        entity,
        limit: 100,
        search
    })
    const currentViewId = searchParams.get('view')
    const viewQuery = useGetView(currentViewId, {
        enabled: !!currentViewId
    })
    const triggerRef = useRef<HTMLSelectElement>(null)
    const isChanged = !!searchParams.toString() && viewQuery.data?.body != searchParams.toString()

    return (
        <Flexbox justify="space-between" fullWidth gap={2}>
            <ComboboxProvider value={search} setValue={setSearch}>
                <SelectProvider
                    value={searchParams.get('view') ? searchParams.toString() : 'all'}
                    setValue={value => {
                        setSearchParams(value == 'all' ? '' : value)
                    }}
                >
                    <SelectTrigger
                        ref={triggerRef}
                        size="sm"
                        displayValue={() => (
                            <>
                                <StyledTriggerLabel>
                                    <span>{t('commons:view')}:</span> {viewQuery.data?.name ?? t('commons:view_all')}
                                </StyledTriggerLabel>
                            </>
                        )}
                        isLoading={viewsQuery.isPending || viewQuery.fetchStatus == 'fetching'}
                    />
                    <SelectComboboxPopover sameWidth={false}>
                        <SelectComboboxInput placeholder={t('commons:search_placeholder')} />
                        {viewsQuery.isPending ? (
                            <Spinner size={12} color={palette.neutral['400']} />
                        ) : viewsQuery.isError ? (
                            <StyledItem value="error" disabled>
                                {t('errors:default')}
                            </StyledItem>
                        ) : (
                            <SelectVirtualList
                                style={{width: 320}}
                                endReached={viewsQuery.fetchNextPage}
                                data={[
                                    {
                                        id: 'all',
                                        name: t('commons:view_all'),
                                        body: '',
                                        visibility: View.shape.visibility.enum.public
                                    },
                                    ...viewsQuery.remappedData
                                ]}
                                overscan={100}
                                itemContent={(_, view) => (
                                    <StyledItem
                                        key={view.id}
                                        value={view.body}
                                        render={<ComboboxItem />}
                                        hasItemCheckbox
                                    >
                                        <span>{view.name}</span>
                                        {view.id == 'all' ? (
                                            false
                                        ) : view.visibility == View.shape.visibility.enum.public ? (
                                            <Users01Icon className="visibility-icon" size={14} />
                                        ) : (
                                            <User01Icon className="visibility-icon" size={14} />
                                        )}
                                        {view.id != 'all' && (
                                            <RowMenu triggerRef={triggerRef} entity={entity} view={view} />
                                        )}
                                    </StyledItem>
                                )}
                            />
                        )}
                    </SelectComboboxPopover>
                </SelectProvider>
            </ComboboxProvider>
            {isChanged && (
                <Flexbox gap={2}>
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => {
                            setSearchParams(viewQuery.data?.body ?? '')
                            if (searchInputRef.current) {
                                searchInputRef.current.value =
                                    new URLSearchParams(viewQuery.data?.body ?? '').get('search') ?? ''
                            }
                        }}
                    >
                        {t('commons:cancel')}
                    </Button>
                    {!searchParams.get('view') ? (
                        <CreateViewDialog entity={entity} />
                    ) : (
                        <SaveButton view={viewQuery.data} entity={entity} />
                    )}
                </Flexbox>
            )}
            <UnsavedBlocker isBlockerActive={isChanged && !!searchParams.get('view')} />
        </Flexbox>
    )
}

const SaveButton = ({view, entity}: {view?: View; entity: ViewEntity}) => {
    const {t} = useTranslation()
    const [searchParams] = useSearchParams()
    const editViewMutation = useEditView({
        onSuccess: () => {
            toast.success(t('commons:view_edited'))
        },
        onError: errorHandler
    })

    if (!view) return null

    const handleSave = () => {
        editViewMutation.mutate({
            urlParams: {id: view.id},
            payload: {
                ...view,
                entity,
                body: searchParams.toString()
            }
        })
    }

    return (
        <StyledSaveWrapper>
            {view.visibility == View.shape.visibility.enum.public ? (
                <SaveViewDialog
                    view={view}
                    trigger={
                        <Button variant="secondary" size="sm">
                            {t('commons:save_for_all')}
                            {editViewMutation.isPending && <Spinner style={{marginLeft: 8}} />}
                        </Button>
                    }
                    isPending={editViewMutation.isPending}
                    onConfirm={handleSave}
                />
            ) : (
                <Button variant="secondary" size="sm" onClick={handleSave}>
                    {t('commons:save_modified')}
                    {editViewMutation.isPending && <Spinner style={{marginLeft: 8}} />}
                </Button>
            )}
            <MenuProvider>
                <MenuButton
                    render={
                        <Button variant="secondary" size="sm" shape="square">
                            <ChevronDownIcon />
                        </Button>
                    }
                />
                <Menu>
                    <SaveViewAsNewDialog view={view} entity={entity} />
                </Menu>
            </MenuProvider>
        </StyledSaveWrapper>
    )
}
